<template>
  <b-modal
    :visible="visible"
    title="Create Service Action"
    size="lg"
    @hidden="handleClose"
    @ok="handleSubmit"
  >
    <b-form @submit.prevent="handleSubmit">
      <b-row>
        <!-- Name -->
        <b-col cols="12">
          <b-form-group
            label="Action Name *"
            label-for="name"
          >
            <b-form-input
              id="name"
              v-model="form.name"
              placeholder="Enter action name"
              :state="errors.name ? false : null"
            />
            <b-form-invalid-feedback v-if="errors.name">
              {{ errors.name[0] }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <!-- Description -->
        <b-col cols="12">
          <b-form-group
            label="Description"
            label-for="description"
          >
            <b-form-textarea
              id="description"
              v-model="form.description"
              placeholder="Enter description"
              rows="3"
              :state="errors.description ? false : null"
            />
            <b-form-invalid-feedback v-if="errors.description">
              {{ errors.description[0] }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <!-- Default Responsibility Type -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            label="Responsibility Type *"
            label-for="responsibility-type"
          >
            <v-select
              v-model="form.default_responsibility_type"
              :options="responsibilityOptions"
              :reduce="option => option.id"
              label="label"
              placeholder="Select responsibility type"
              :state="errors.default_responsibility_type ? false : null"
            />
            <b-form-invalid-feedback
              v-if="errors.default_responsibility_type"
              :state="false"
            >
              {{ errors.default_responsibility_type[0] }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <!-- Estimated Hours -->
        <b-col
          cols="12"
          md="6"
        >
          <b-form-group
            label="Estimated Hours"
            label-for="estimated-hours"
          >
            <b-form-input
              id="estimated-hours"
              v-model.number="form.estimated_hours"
              type="number"
              step="0.5"
              placeholder="Enter estimated hours"
              :state="errors.estimated_hours ? false : null"
            />
            <b-form-invalid-feedback v-if="errors.estimated_hours">
              {{ errors.estimated_hours[0] }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <!-- Parent Action -->
        <b-col
          v-if="availableActions.length > 0"
          cols="12"
        >
          <b-form-group
            label="Parent Action"
            label-for="parent-action"
          >
            <v-select
              v-model="form.parent_action_id"
              :options="parentActionOptions"
              :reduce="option => option.value"
              label="label"
              placeholder="Select parent action (optional)"
              :state="errors.parent_action_id ? false : null"
            />
            <b-form-invalid-feedback
              v-if="errors.parent_action_id"
              :state="false"
            >
              {{ errors.parent_action_id[0] }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <!-- Order -->
        <b-col cols="12">
          <b-form-group
            label="Order"
            label-for="order"
          >
            <b-form-input
              id="order"
              v-model.number="form.order"
              type="number"
              placeholder="Enter order position"
              :state="errors.order ? false : null"
            />
            <b-form-invalid-feedback v-if="errors.order">
              {{ errors.order[0] }}
            </b-form-invalid-feedback>
            <small class="text-muted">Lower numbers appear first</small>
          </b-form-group>
        </b-col>

        <!-- Is Milestone -->
        <b-col cols="12">
          <b-form-group>
            <b-form-checkbox
              v-model="form.is_milestone"
              value="1"
              unchecked-value="0"
            >
              Is Milestone
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>

    <template #modal-footer="{ ok, cancel }">
      <b-button
        variant="outline-secondary"
        @click="cancel()"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        :disabled="loading"
        @click="ok()"
      >
        <b-spinner
          v-if="loading"
          small
          class="mr-50"
        />
        <feather-icon
          v-else
          icon="SaveIcon"
          class="mr-50"
        />
        <span class="align-middle">Create Action</span>
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormInvalidFeedback,
  BFormCheckbox,
  BButton,
  BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'ServiceActionFormModal',
  components: {
    BModal,
    BForm,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormInvalidFeedback,
    BFormCheckbox,
    BButton,
    BSpinner,
    vSelect,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    serviceId: {
      type: Number,
      default: null,
    },
    availableActions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      form: {
        service_id: null,
        name: '',
        description: '',
        default_responsibility_type: 'required',
        estimated_hours: null,
        parent_action_id: null,
        order: null,
        is_milestone: 0,
      },
      errors: {},
      loading: false,
      responsibilityOptions: [
        { label: 'Required', value: 'required', id: 1 },
        { label: 'Optional', value: 'optional', id: 2 },
        { label: 'Client Responsibility', value: 'client_responsibility', id: 3 },
      ],
    }
  },
  computed: {
    parentActionOptions() {
      return this.availableActions.map(action => ({
        label: action.name,
        value: action.id,
      }))
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.initForm()
      }
    },
  },
  methods: {
    ...mapActions('serviceActions', ['createServiceAction']),
    initForm() {
      this.form = {
        service_id: this.serviceId,
        name: '',
        description: '',
        default_responsibility_type: 'required',
        estimated_hours: null,
        parent_action_id: null,
        order: null,
        is_milestone: 0,
      }
      this.errors = {}
    },
    async handleSubmit(bvModalEvt) {
      if (bvModalEvt) {
        bvModalEvt.preventDefault()
      }

      try {
        this.loading = true
        this.errors = {}

        await this.createServiceAction(this.form)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'Service action created successfully',
          },
        })

        this.$emit('saved')
        this.handleClose()
      } catch (error) {
        if (error.response?.status === 422) {
          this.errors = error.response.data.errors || {}
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error.response?.data?.message || 'Failed to create service action',
          },
        })
      } finally {
        this.loading = false
      }
    },
    handleClose() {
      this.$emit('close')
      this.initForm()
    },
  },
}
</script>
